import cn from 'classnames';
import React from 'react';

import Banner from '@/components/Banner/Banner';
import {AppButton} from '@/components/Button';
import {PUSH_NOTIFICATIONS_PROMPT_TYPES} from '@/consts/pushNotifications/pushNotificationsPromptTypes';
import useConfigExternalLink from '@/hooks/externalLink/useConfigExternalLink';
import useRefetchExternalConsumerProfile from '@/hooks/useRefetchExternalConsumerProfile';
import {getAriaLabel} from '@/services/accessibilityService';
import amplitudeHomePageTracking from '@/services/analytics/amplitude/amplitudeHomePageTracking';
import googleAnalyticsService from '@/services/analytics/ga/googleAnalyticsService';
import {getLocalizedStrings} from '@/services/localization/localizationService';
import {schedulePushPromptIfNeeded} from '@/services/pushNotification/pushNotificationPromptService';
import helpers from '@/utils/helpers';
import {twx} from '@/utils/tailwindUtils';

import {TopBannerGreeting, WelcomeContent} from '../components';
import HomePageLoyaltyBanner from './components/HomePageLoyaltyBanner';
import HomePageLoyaltyButtonsBar from './components/HomePageLoyaltyButtonsBar';
import useHomePageTopBannerData from './hooks/useHomePageTopBannerData';
import useWelcomeTopBannerAnimation from './hooks/useWelcomeTopBannerAnimation';

const HomePageTopBanner = ({id}) => {
    const {
        bannerGreetingRef,
        welcomeGreetingRef,
        isWelcomeModeInProgress,
        isAnimationStarted,
        startAnimation,
        translation,
        bannerAnimationClassName,
        greetingAnimationClassName,
        greetingAnimationStyle,
        bannerContentAnimationClassName,
        loyaltyButtonsBarAnimationClassName,
    } = useWelcomeTopBannerAnimation();
    const {isLoyaltyBanner, bannerData, loyaltyButtons} = useHomePageTopBannerData({id});

    const {
        titleKey,
        descriptionKey,
        buttonActionKey,
        imageMedia,
        action,
        isGradient,
        isDarkBannerText,
        amplitudeTrackingValue,
        actionAriaLabelKey,
    } = bannerData || {};
    const ariaLabel = getAriaLabel(actionAriaLabelKey);

    const {redirectToExternalLink} = useConfigExternalLink(action);
    const isShowButton = buttonActionKey && redirectToExternalLink;

    const localizedStrings = getLocalizedStrings();
    const isLoyaltyButtonsBarEnabled = loyaltyButtons?.length;

    useRefetchExternalConsumerProfile();

    if (!bannerData) return null;

    const onButtonClick = () => {
        if (isLoyaltyBanner) {
            schedulePushPromptIfNeeded(PUSH_NOTIFICATIONS_PROMPT_TYPES.LOYALTY_CLICK);
        }

        amplitudeHomePageTracking.trackHomePageTopBannerButtonClick(amplitudeTrackingValue);
        googleAnalyticsService.trackHomeTopBannerClick(amplitudeTrackingValue);
        helpers.runFunction(redirectToExternalLink);
    };

    return (
        <div
            className={cn(
                '-mb-2 bg-romance md:mb-6 md:bg-transparent',
                isLoyaltyButtonsBarEnabled && 'pb-14 md:pb-8 ml:pb-5 lg:pb-10'
            )}
        >
            <Banner
                className={twx(
                    'h-[518px] w-full font-bold text-romance md:h-[434px] xl:h-[604px] 2xl:h-[749px]',
                    bannerAnimationClassName
                )}
                imgClassName='object-top animate-fade-in'
                imageMedia={imageMedia}
                isGradient={isGradient}
                isDarkBannerText={isDarkBannerText}
                gradient='linear-gradient(180deg, rgba(52, 48, 61, 0.00) 0.01%, rgba(48, 44, 56, 0.17) 8.34%, rgba(33, 31, 39, 0.78) 59.37%, #1C1A21 99.99%)'
            >
                <TopBannerGreeting
                    greetingRef={bannerGreetingRef}
                    animationClassName={greetingAnimationClassName}
                    animationStyle={greetingAnimationStyle}
                />
                <div
                    className={twx(
                        'absolute bottom-8 left-0 right-0 max-w-2xl px-4 text-start md:px-8 lg:px-16 xl:mx-auto xl:box-content',
                        {'bottom-16 md:bottom-20 ml:bottom-25 lg:bottom-20': isLoyaltyButtonsBarEnabled},
                        bannerContentAnimationClassName
                    )}
                >
                    {isLoyaltyBanner && (
                        <HomePageLoyaltyBanner onClick={onButtonClick} bannerData={bannerData} aria-label={ariaLabel} />
                    )}
                    {!isLoyaltyBanner && (
                        <>
                            <h1 className='text-32 md:mb-2'>{localizedStrings[titleKey]}</h1>
                            <p
                                dangerouslySetInnerHTML={{__html: localizedStrings[descriptionKey]}}
                                className='font-normal'
                            />
                        </>
                    )}
                    {isShowButton && (
                        <AppButton
                            primary
                            dark
                            className='mt-8 font-normal md:mt-6'
                            aria-label={ariaLabel}
                            onClick={onButtonClick}
                        >
                            {localizedStrings[buttonActionKey]}
                        </AppButton>
                    )}
                </div>
                {isLoyaltyButtonsBarEnabled && (
                    <div className='absolute -bottom-14 left-0 right-0 flex max-w-2xl items-center px-4 md:-bottom-12 md:px-8 ml:-bottom-8 lg:-bottom-12 lg:px-16 xl:mx-auto xl:box-content'>
                        <HomePageLoyaltyButtonsBar
                            buttons={loyaltyButtons}
                            className={loyaltyButtonsBarAnimationClassName}
                        />
                    </div>
                )}
                {isWelcomeModeInProgress && (
                    <WelcomeContent
                        isAnimationStarted={isAnimationStarted}
                        onClick={startAnimation}
                        greetingRef={welcomeGreetingRef}
                        greetingTranslation={translation}
                        isDarkBannerText={isDarkBannerText}
                    />
                )}
            </Banner>
        </div>
    );
};

export default HomePageTopBanner;
